import { useCallback, useEffect, useRef, useState } from 'react';
import {
  ActionIcon,
  Alert,
  Badge,
  Box,
  Button,
  Center,
  Group,
  Loader,
  Menu,
  Modal,
  Stack,
  Text,
} from '@mantine/core';
import { PillTabList } from '@shared/ui/PillTabList';
import {
  IconDots,
  IconInfoCircle,
  IconPhone,
  IconPhoneIncoming,
  IconPhoneOutgoing,
  IconRefresh,
  IconTrash,
  IconVideo,
} from '@tabler/icons-react';
import { fetchCallHistory, setCallHistoryHidden } from '@domain/call/api';
import { isCallMessageType, parseCallMessage } from '@domain/call/message';
import type { CallHistoryEntry, CallHistoryFilter } from '@domain/call/types';
import { UserAvatar } from '@shared/ui/UserAvatar';
import { getErrorMessage } from '@shared/utils/ui';
import { eventBus } from '../../../eventBus';
import { WorkspaceEmptyState } from '../components/WorkspaceEmptyState';

type Props = {
  apiBase: string;
  onRedial: (entry: CallHistoryEntry) => void;
};

const filters: Array<{ label: string; value: CallHistoryFilter }> = [
  { label: '全部', value: 'all' },
  { label: '未接', value: 'missed' },
  { label: '语音', value: 'audio' },
  { label: '视频', value: 'video' },
  { label: '群组', value: 'group' },
];

const outcomeLabels: Record<CallHistoryEntry['terminalOutcome'], string> = {
  completed: '通话结束',
  declined: '已拒绝',
  cancelled: '已取消',
  missed: '未接听',
  busy: '对方忙线',
  failed: '连接失败',
  disconnected: '连接中断',
};

function durationLabel(durationMs?: number) {
  if (durationMs === undefined) return '';
  const totalSeconds = Math.max(0, Math.floor(durationMs / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${String(seconds).padStart(2, '0')}`;
}

function contextLabel(entry: CallHistoryEntry) {
  return entry.scope === 'direct'
    ? entry.context.peer?.displayName || `用户 #${entry.context.peer?.userId ?? ''}`
    : entry.context.name || `群组 #${entry.context.groupId ?? ''}`;
}

export function CallHistoryStage({ apiBase, onRedial }: Props) {
  const [filter, setFilter] = useState<CallHistoryFilter>('all');
  const [items, setItems] = useState<CallHistoryEntry[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [state, setState] = useState<'loading' | 'ready' | 'error'>('loading');
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<CallHistoryEntry | null>(null);
  const [hidingId, setHidingId] = useState<string | null>(null);
  const requestGeneration = useRef(0);

  const load = useCallback(async (nextFilter: CallHistoryFilter, nextCursor?: string) => {
    const generation = ++requestGeneration.current;
    setState('loading');
    setError(null);
    try {
      const page = await fetchCallHistory(apiBase, {
        filter: nextFilter,
        limit: 50,
        cursor: nextCursor,
      });
      if (generation !== requestGeneration.current) return;
      setItems((current) => (nextCursor ? [...current, ...page.items] : page.items));
      setCursor(page.nextCursor);
      setHasMore(page.hasMore);
      setState('ready');
    } catch (loadError) {
      if (generation !== requestGeneration.current) return;
      setError(getErrorMessage(loadError));
      setState('error');
    }
  }, [apiBase]);

  useEffect(() => {
    setItems([]);
    setCursor(null);
    setHasMore(false);
    setSelected(null);
    void load(filter);
    return () => {
      requestGeneration.current += 1;
    };
  }, [filter, load]);

  useEffect(() => eventBus.on('NewMsgEvent', (message) => {
    if (!isCallMessageType(message.msgType)) return;
    if (!parseCallMessage(message.msgData)?.callId) return;
    void load(filter);
  }), [filter, load]);

  const hide = useCallback(async (entry: CallHistoryEntry) => {
    setHidingId(entry.callId);
    try {
      await setCallHistoryHidden(apiBase, entry.callId, true);
      setItems((current) => current.filter((item) => item.callId !== entry.callId));
      setSelected((current) => (current?.callId === entry.callId ? null : current));
    } catch (hideError) {
      setError(getErrorMessage(hideError));
    } finally {
      setHidingId(null);
    }
  }, [apiBase]);

  return (
    <Stack gap="lg">
      <PillTabList
        value={filter}
        onChange={(value) => setFilter(value as CallHistoryFilter)}
        aria-label="筛选通话记录"
        items={filters.map((f) => ({ value: f.value, children: f.label }))}
      />

      {error && (
        <Alert color="red" title="暂时无法读取通话记录">
          <Group justify="space-between">
            <Text size="sm">{error}</Text>
            <Button variant="subtle" size="xs" leftSection={<IconRefresh size={15} />} onClick={() => void load(filter)}>
              重试
            </Button>
          </Group>
        </Alert>
      )}

      {state === 'loading' && items.length === 0 ? (
        <Center className="call-history-state"><Loader color="terracotta" /></Center>
      ) : items.length === 0 ? (
        <Center className="call-history-state">
          <WorkspaceEmptyState
            icon={<IconPhone size={30} stroke={2} />}
            message="暂无通话记录"
          />
        </Center>
      ) : (
        <Stack gap={6}>
          {items.map((entry) => {
            const directPeer = entry.context.peer;
            const title = contextLabel(entry);
            const danger = entry.missed || ['failed', 'disconnected'].includes(entry.terminalOutcome);
            const MediaIcon = entry.mediaKind === 'video' ? IconVideo : IconPhone;
            return (
              <Box
                key={entry.callId}
                p="md"
                role="button"
                tabIndex={0}
                className="call-history-entry locs-hover-lift"
                onClick={() => setSelected(entry)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') setSelected(entry);
                }}
                style={{
                  borderRadius: 'var(--mantine-radius-lg)',
                  background: 'var(--locs-panel)',
                  cursor: 'pointer',
                }}
              >
                <Group wrap="nowrap" align="center">
                  <UserAvatar
                    id={directPeer?.userId ?? entry.context.groupId ?? entry.callId}
                    name={title}
                    avatarKind={entry.scope === 'direct' ? 'human' : 'group'}
                    size={46}
                  />
                  <Stack gap={3} style={{ minWidth: 0, flex: 1 }}>
                    <Group gap={7} wrap="nowrap">
                      <Text fw={800} truncate>{title}</Text>
                      {entry.missed && <Badge size="xs" color="red" variant="light">未接</Badge>}
                    </Group>
                    <Group gap={5} c={danger ? 'red.7' : 'dimmed'} wrap="nowrap">
                      {entry.direction === 'incoming' ? <IconPhoneIncoming size={15} /> : <IconPhoneOutgoing size={15} />}
                      <Text size="xs" truncate>
                        {outcomeLabels[entry.terminalOutcome]}
                        {entry.durationMs !== undefined ? ` · ${durationLabel(entry.durationMs)}` : ''}
                      </Text>
                    </Group>
                  </Stack>
                  <Stack gap={3} align="flex-end">
                    <Text size="xs" c="dimmed">{new Date(entry.endedAtMs).toLocaleString()}</Text>
                    <Menu position="bottom-end" withinPortal>
                      <Menu.Target>
                        <ActionIcon
                          aria-label={`管理与 ${title} 的通话记录`}
                          variant="subtle"
                          color="gray"
                          onClick={(event) => event.stopPropagation()}
                        >
                          <IconDots size={18} />
                        </ActionIcon>
                      </Menu.Target>
                      <Menu.Dropdown onClick={(event) => event.stopPropagation()}>
                        <Menu.Item leftSection={<IconInfoCircle size={16} />} onClick={() => setSelected(entry)}>
                          详情
                        </Menu.Item>
                        {entry.scope === 'direct' && directPeer && (
                          <Menu.Item leftSection={MediaIcon ? <MediaIcon size={16} /> : undefined} onClick={() => onRedial(entry)}>呼叫</Menu.Item>
                        )}
                        <Menu.Item
                          color="red"
                          leftSection={<IconTrash size={16} />}
                          disabled={hidingId === entry.callId}
                          onClick={() => void hide(entry)}
                        >
                          删除记录
                        </Menu.Item>
                      </Menu.Dropdown>
                    </Menu>
                  </Stack>
                </Group>
              </Box>
            );
          })}
          {hasMore && cursor && (
            <Button variant="default" loading={state === 'loading'} onClick={() => void load(filter, cursor)}>
              加载更早的记录
            </Button>
          )}
        </Stack>
      )}

      <Modal opened={selected !== null} onClose={() => setSelected(null)} title="通话详情" centered>
        {selected && (() => {
          const selectedPeer = selected.context.peer;
          const selectedTitle = contextLabel(selected);
          const SelectedMediaIcon = selected.mediaKind === 'video' ? IconVideo : IconPhone;
          const selectedDanger = selected.missed || ['failed', 'disconnected'].includes(selected.terminalOutcome);
          return (
          <Stack gap="md">
            <Group wrap="nowrap" align="center" gap="md">
              <UserAvatar
                id={selected.scope === 'direct' ? (selectedPeer?.userId ?? selected.callId) : (selected.context.groupId ?? selected.callId)}
                name={selectedTitle}
                avatarKind={selected.scope === 'direct' ? 'human' : 'group'}
                size={52}
              />
              <Stack gap={3} style={{ minWidth: 0, flex: 1 }}>
                <Text fw={850} size="lg" truncate>{selectedTitle}</Text>
                <Group gap={5} c={selectedDanger ? 'red.7' : 'dimmed'} wrap="nowrap">
                  <SelectedMediaIcon size={13} />
                  <Text size="sm" truncate>{outcomeLabels[selected.terminalOutcome]}</Text>
                </Group>
              </Stack>
            </Group>
            <Stack gap="xs" px="md">
              <Group justify="space-between"><Text size="sm" c="dimmed">方向</Text><Text size="sm" fw={700}>{selected.direction === 'incoming' ? '呼入' : '呼出'}</Text></Group>
              <Group justify="space-between"><Text size="sm" c="dimmed">时间</Text><Text size="sm" fw={700}>{new Date(selected.endedAtMs).toLocaleString()}</Text></Group>
              <Group justify="space-between"><Text size="sm" c="dimmed">时长</Text><Text size="sm" fw={700}>{selected.durationMs === undefined ? '未接通' : durationLabel(selected.durationMs)}</Text></Group>
            </Stack>
            <Group justify="center" gap="sm">
              <Button color="red" variant="subtle" leftSection={<IconTrash size={18} />} loading={hidingId === selected.callId} onClick={() => void hide(selected)}>
                删除记录
              </Button>
              {selected.scope === 'direct' && selected.context.peer && (
                <Button
                  variant="subtle"
                  leftSection={selected.mediaKind === 'video' ? <IconVideo size={18} /> : <IconPhone size={18} />}
                  onClick={() => onRedial(selected)}
                >
                  呼叫
                </Button>
              )}
            </Group>
          </Stack>
          );
        })()}
      </Modal>
    </Stack>
  );
}
